"use client";

import React from "react";
import { useStore } from "zustand";
import { createUserStore, initUserStore } from "./store";
import type { UserStore } from "./store";

export type UserStoreApi = ReturnType<typeof createUserStore>;

export const UserStoreContext = React.createContext<UserStoreApi | undefined>(
  undefined,
);

export interface UserStoreProviderProps {
  children: React.ReactNode;
}

export const UserStoreProvider = ({ children }: UserStoreProviderProps) => {
  const storeRef = React.useRef<UserStoreApi>(createUserStore(initUserStore()));

  return (
    <UserStoreContext.Provider value={storeRef.current}>
      {children}
    </UserStoreContext.Provider>
  );
};

/**
 * This store is used to store the user's information and the authentication state.
 * This should only store auth information pulled from Firebase, _not_ the Greater User identity.
 *
 * To fetch the Greater User, @see user-query.ts
 */
export const useUserStore = <T,>(selector: (store: UserStore) => T): T => {
  const counterStoreContext = React.useContext(UserStoreContext);

  if (!counterStoreContext) {
    throw new Error(`useUserStore must be used within UserStoreProvider`);
  }

  return useStore(counterStoreContext, selector);
};
